"use client";

import React, { useState, useEffect } from "react";
import { Search, Menu, X, ChevronRight, HelpCircle } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ProductsSearch, useWPQueryForSearch } from "@/components/shop/ProductsSearch";
import { EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { cn } from "@/lib/utils";
import { WvcLogo } from '@/integrations/wordpress/WvcLogo';
function SearchResults() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="p-4 text-xs font-sans uppercase text-muted-foreground animate-pulse">Searching services...</div>;
  }
  if (error) {
    return <div className="p-4 text-xs font-sans uppercase text-destructive">Connection Error</div>;
  }
  if (!products.length) {
    return <div className="p-4 text-xs font-sans uppercase text-muted-foreground">No services found.</div>;
  }
  return <div data-section-id="18822" className="grid grid-cols-1 gap-0 divide-y divide-border">
      {products.slice(0, 5).map((product, i) => <Link key={product.id} to={product.link} className="group flex items-center gap-4 p-3 hover:bg-accent/50 transition-colors" data-index={i}>
          {product.featured_image_url && <div className="h-10 w-10 shrink-0 overflow-hidden bg-muted grayscale group-hover:grayscale-0 transition-all">
              <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover" loading="lazy" />
            </div>}
          <div className="min-w-0 flex-1">
            <h4 className="truncate font-sans text-xs uppercase tracking-wider text-foreground group-hover:text-primary transition-colors">
              {product.title}
            </h4>
            <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
              <span>${product.price}</span>
            </div>
          </div>
          <ChevronRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
        </Link>)}
    </div>;
}
function SearchOverlay({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [searchValue, setSearchValue] = useState("");
  const [wp_query, hasSearchQuery] = useWPQueryForSearch(searchValue);
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full border-b border-border bg-background/95 backdrop-blur-xl animate-in slide-in-from-top-2 fade-in duration-200 shadow-2xl z-40">
      <EcommerceProductsProvider wp_query={wp_query}>
        <div className="container mx-auto max-w-3xl p-4">
          <div className="relative flex items-center">
            <Search className="absolute left-3 h-4 w-4 text-muted-foreground" />
            <ProductsSearch value={searchValue} onChange={setSearchValue} className="w-full" placeholder="SEARCH SERVICES..." />
            <Button variant="ghost" size="icon" onClick={onClose} className="ml-2 shrink-0">
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {hasSearchQuery && <div className="mt-4 max-h-[60vh] overflow-y-auto border border-border bg-background">
              <SearchResults />
            </div>}
        </div>
      </EcommerceProductsProvider>
    </div>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) return <div className="p-8 text-center font-sans text-xs animate-pulse">CONNECTING...</div>;
  return <nav className="flex flex-col items-center justify-center space-y-4 p-6 pb-20">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="font-sans text-lg uppercase tracking-widest hover:text-primary transition-colors w-full text-center py-3 border-b border-foreground/10 last:border-0" onClick={onClose} data-index={i}>
          {item.label}
        </Link>)}
    </nav>;
}
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-8 animate-pulse">
      <div className="h-4 w-16 bg-muted rounded-md" />
      <div className="h-4 w-16 bg-muted rounded-md" />
    </div>;
  }
  return <>
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="relative font-sans text-xs uppercase tracking-[0.2em] text-foreground/80 transition-all hover:text-foreground group overflow-hidden" data-index={i}>
          <span className="relative z-10 group-hover:text-primary transition-colors duration-200">
            {item.label}
          </span>
          
          <span className="absolute top-0 left-0 -z-10 translate-x-[-2px] opacity-0 group-hover:opacity-70 group-hover:animate-pulse text-primary/40 mix-blend-screen">
            {item.label}
          </span>
          <span className="absolute top-0 left-0 -z-10 translate-x-[2px] opacity-0 group-hover:opacity-70 group-hover:animate-pulse text-primary/20 mix-blend-screen delay-75">
            {item.label}
          </span>
        </Link>)}
    </>;
}
export default function PulseThreadEntry() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full border-b border-transparent transition-all duration-300 dark bg-background text-foreground overflow-x-hidden", isScrolled ? "bg-background/80 backdrop-blur-xl border-border/40" : "bg-background")}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-center relative">
        
        <nav className={cn("absolute left-4 hidden md:flex items-center gap-8 transition-all duration-300")}>
          <MenuProvider menu_id="40" className="flex items-center gap-4 md:gap-8">
            <DesktopMenuConsumer />
          </MenuProvider>
        </nav>

        <div className="absolute top-0 right-0 flex md:hidden items-center h-20 px-3 z-[60] gap-1">
          
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="h-12 w-12 hover:bg-transparent hover:text-primary">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>

        <div className="flex-shrink-0 flex-1 text-center sm:text-left flex flex-col items-center">
          <Link to="/" className="block opacity-90 hover:opacity-100 transition-opacity">
            <WvcLogo className="h-8 w-auto" />
          </Link>
        </div>

        <div className="absolute right-4 hidden md:flex items-center gap-2 md:gap-4 max-w-[calc(100%-8px)]">
        </div>
      </div>

      <SearchOverlay isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />

      {isMobileMenuOpen && <div className="fixed inset-0 z-40 bg-background/98 pt-24 pb-12 backdrop-blur-2xl md:hidden animate-in fade-in slide-in-from-top-full duration-500 overflow-y-auto shadow-2xl max-w-full">
          <MenuProvider menu_id="40">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>}
    </header>;
}